import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './adminpanel.css';
import { loginUser, changeAdminPassword } from '../utils/auth';

const AdminPanel = ({ socket }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [analyses, setAnalyses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingAnalysis, setEditingAnalysis] = useState(null);
  const [newAnalysis, setNewAnalysis] = useState({
    nom: '',
    prix: '',
    type: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await loginUser('admin', password);
    if (result.success) {
      setIsAuthenticated(true);
    } else {
      alert(result.message || 'Mot de passe incorrect');
      setPassword('');
    }
  };

  // Charger les analyses via socket
  const loadAnalysesData = () => {
    console.log('🔄 Admin: Loading analyses data from server...');
    console.log('🔌 Socket disponible:', !!socket);
    console.log('🔌 Socket ID:', socket?.id);
    console.log('🔌 Socket connecté:', socket?.connected);
    
    setLoading(true);
    setError(null);
    setDataLoaded(false);
    
    if (!socket) {
        const errorMsg = 'Socket non disponible';
        console.error('❌', errorMsg);
        setError(errorMsg);
        setLoading(false);
        return;
    }

    if (!socket.connected) {
        const errorMsg = 'Socket non connecté';
        console.error('❌', errorMsg);
        setError(errorMsg);
        setLoading(false);
        return;
    }

    console.log('📤 Émission de load_analyses...');
    
    // Émettre l'événement pour charger les analyses
    socket.emit('load_analyses', (response) => {
        console.log('📩 Callback reçu du serveur:', response);
        
        if (response && response.success) {
            const analysesData = response.data?.analyses || [];
            const categoriesData = response.data?.categories || [];
            
            console.log('✅ Données reçues - Analyses:', analysesData.length);
            console.log('✅ Données reçues - Catégories:', categoriesData.length);
            
            setAnalyses(analysesData);
            setCategories(categoriesData);
            setDataLoaded(true);
            setSaveMessage('✅ Données chargées depuis le serveur');
            setTimeout(() => setSaveMessage(''), 3000);
        } else {
            const errorMsg = response?.message || 'Erreur inconnue du serveur';
            console.error('❌ Erreur serveur:', errorMsg);
            setError('Erreur serveur: ' + errorMsg);
        }
        
        setLoading(false);
    });

    // Vérifier si le callback n'est jamais appelé
    setTimeout(() => {
        if (loading) {
            console.error('⏰ TIMEOUT: Aucune réponse du serveur après 5s');
            console.log('🔍 État socket:', {
                connected: socket.connected,
                id: socket.id,
                active: socket.active
            });
            setError('Timeout: Le serveur ne répond pas à load_analyses');
            setLoading(false);
        }
    }, 5000);
};

  // Écouter les mises à jour en temps réel
  useEffect(() => {
    if (!socket) {
      console.warn('⚠️ Socket non disponible pour les mises à jour en temps réel');
      return;
    }

    const handleAnalysesUpdated = (data) => {
      console.log('🔄 Mise à jour en temps réel reçue:', data);
      // Vérifier la structure des données
      const analysesData = data?.analyses || data?.data?.analyses || [];
      const categoriesData = data?.categories || data?.data?.categories || [];

      console.log('📊 Mise à jour - Analyses:', analysesData.length);
      console.log('📊 Mise à jour - Catégories:', categoriesData.length);

      setAnalyses(analysesData);
      setCategories(categoriesData);
    };

    socket.on('analyses_updated', handleAnalysesUpdated);

    return () => {
      socket.off('analyses_updated', handleAnalysesUpdated);
    };
  }, [socket]);

// Dans AdminPanel.js, dans le useEffect qui charge les données
useEffect(() => {
  if (isAuthenticated && socket) {
    console.log('🔑 Administrateur authentifié');
    socket.emit('module_connected', { module: 'Administration', socketId: socket.id });
    console.log('🔑 Utilisateur authentifié, chargement des données...');
    loadAnalysesData();
  }
}, [isAuthenticated, socket]);

  const handleEdit = (analysis) => {
    setEditingAnalysis({ ...analysis });
    setSaveMessage('');
  };

  const handleSaveEdit = () => {
    if (!editingAnalysis?.nom || !editingAnalysis?.prix || !editingAnalysis?.type) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    if (!socket) {
      setSaveMessage('❌ Erreur: Socket non disponible');
      return;
    }

    console.log('💾 Sauvegarde analyse:', editingAnalysis);

    socket.emit('update_analysis', editingAnalysis, (response) => {
      console.log('📩 Réponse update_analysis:', response);
      if (response && response.success) {
        setEditingAnalysis(null);
        setSaveMessage('✅ Modification enregistrée avec succès!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        const errorMsg = response?.message || 'Erreur lors de la mise à jour';
        setSaveMessage('❌ ' + errorMsg);
      }
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette analyse?')) {
      if (!socket) {
        setSaveMessage('❌ Erreur: Socket non disponible');
        return;
      }

      console.log('🗑️ Suppression analyse ID:', id);

      socket.emit('delete_analysis', id, (response) => {
        console.log('📩 Réponse delete_analysis:', response);
        if (response && response.success) {
          setSaveMessage('✅ Analyse supprimée avec succès!');
          setTimeout(() => setSaveMessage(''), 3000);
        } else {
          const errorMsg = response?.message || 'Erreur lors de la suppression';
          setSaveMessage('❌ ' + errorMsg);
        }
      });
    }
  };

  const handleNewAnalysisChange = (field, value) => {
    setNewAnalysis(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAnalysis = () => {
    if (!newAnalysis.nom || !newAnalysis.prix || !newAnalysis.type) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    if (!socket) {
      setSaveMessage('❌ Erreur: Socket non disponible');
      return;
    }

    console.log('➕ Ajout analyse:', newAnalysis);

    socket.emit('add_analysis', newAnalysis, (response) => {
      console.log('📩 Réponse add_analysis:', response);
      if (response && response.success) {
        setNewAnalysis({ nom: '', prix: '', type: '' });
        setSaveMessage('✅ Analyse ajoutée avec succès!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        const errorMsg = response?.message || 'Erreur lors de l\'ajout';
        setSaveMessage('❌ ' + errorMsg);
      }
    });
  };

  const handleEditingAnalysisChange = (field, value) => {
    if (!editingAnalysis) return;

    setEditingAnalysis(prev => ({
      ...prev,
      [field]: field === 'prix' ? parseInt(value) || 0 : value
    }));
  };

  const handleResetData = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser toutes les analyses? Cette action est irréversible.')) {
      if (!socket) {
        setSaveMessage('❌ Erreur: Socket non disponible');
        return;
      }

      console.log('🔄 Réinitialisation analyses');

      socket.emit('reset_analyses', (response) => {
        console.log('📩 Réponse reset_analyses:', response);
        if (response && response.success) {
          setSaveMessage('✅ Analyses réinitialisées avec succès!');
          setTimeout(() => setSaveMessage(''), 3000);
        } else {
          const errorMsg = response?.message || 'Erreur lors de la réinitialisation';
          setSaveMessage('❌ ' + errorMsg);
        }
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage('❌ Veuillez remplir tous les champs');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordMessage('❌ Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage('❌ Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    setChangingPassword(true);
    try {
      const result = await changeAdminPassword(currentPassword, newPassword);
      if (result.success) {
        setPasswordMessage('✅ Mot de passe modifié avec succès. Veuillez vous reconnecter.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowChangePassword(false);
          handleLogout();
        }, 2500);
      } else {
        setPasswordMessage(`❌ ${result.message || 'Échec du changement de mot de passe'}`);
      }
    } catch (error) {
      setPasswordMessage('❌ Erreur réseau lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setAnalyses([]);
    setCategories([]);
    setDataLoaded(false);
    setSaveMessage('');
    setShowChangePassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordMessage('');
  };

  const handleBackToCaisse = () => {
    navigate('/caisse');
  };

  const getAnalysesByCategory = (category) => {
    return analyses.filter(analysis => analysis.type === category);
  };

  const handleBackHome = () => {
    navigate('/');
  };


  // Écran de connexion
  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-form">
          <div className="admin-login-header">
            <h2>Accès Administrateur</h2>
            <p>Gestion des Analyses de Laboratoire</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="password">Mot de passe administrateur</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez le mot de passe"
                required
              />
            </div>

            <button type="submit" className="login-btn">
              Se connecter
            </button>

            <button
              type="button"
              className="back-btn"
              onClick={handleBackToCaisse}
            >
              Retour à la caisse
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Interface administrateur
  return (
    <div className="admin-panel">
      <div className="admin-header">

        <div className="admin-actions">
          <div className='flex-row'>
            <button className="reload-btn" onClick={loadAnalysesData} disabled={loading}>
              {loading ? 'Chargement...' : 'Actualiser'}
            </button>
            <button className="reset-btn" onClick={handleResetData}>
              Réinitialiser
            </button>
            <button className="change-password-btn" onClick={() => setShowChangePassword(!showChangePassword)}>
              🔑 Mot de passe
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              Déconnexion
            </button>
            <button className="back-btn" onClick={handleBackHome}>
              Retour
            </button>
          </div>

        </div>
      </div>

      {/* Message de sauvegarde */}
      {saveMessage && (
        <div className={`save-message ${saveMessage.includes('✅') ? 'success' : 'error'}`}>
          {saveMessage}
        </div>
      )}

      {/* Formulaire de changement de mot de passe */}
      {showChangePassword && (
        <div className="change-password-section">
          <h3>🔐 Changer le mot de passe administrateur</h3>
          <form onSubmit={handleChangePassword} className="change-password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Mot de passe actuel</label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Entrez le mot de passe actuel"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">Nouveau mot de passe</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 caractères"
                required
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Répétez le nouveau mot de passe"
                required
                minLength={8}
              />
            </div>
            <div className="change-password-actions">
              <button type="submit" className="save-password-btn" disabled={changingPassword}>
                {changingPassword ? 'Modification...' : '💾 Enregistrer'}
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordMessage('');
                }}
                disabled={changingPassword}
              >
                Annuler
              </button>
            </div>
          </form>
          {passwordMessage && (
            <div className={`password-message ${passwordMessage.includes('✅') ? 'success' : 'error'}`}>
              {passwordMessage}
            </div>
          )}
        </div>
      )}

      {/* Debug info */}
      <div className="debug-info">
        <p>
          Socket: {socket ? '✅ Connecté' : '❌ Déconnecté'} |
          Analyses: {analyses.length} |
          Catégories: {categories.length} |
          Chargement: {loading ? '🔄' : '✅'} |
          Données: {dataLoaded ? '✅' : '❌'}
        </p>
      </div>

      <div className="admin-content">
        {loading && (
          <div className="loading-state">
            <p>Chargement des données depuis le serveur...</p>
            <p>Vérifiez la console pour les détails</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>Erreur: {error}</p>
            <div className="error-actions">
              <button onClick={loadAnalysesData} className="retry-btn" disabled={loading}>
                Réessayer
              </button>
              <button onClick={() => setError(null)} className="cancel-btn">
                Ignorer
              </button>
            </div>
          </div>
        )}

        {!loading && !error && dataLoaded && analyses.length === 0 && (
          <div className="empty-state">
            <p>Aucune analyse disponible. Ajoutez votre première analyse.</p>
            <button onClick={handleResetData} className="retry-btn">
              Charger les données par défaut
            </button>
          </div>
        )}

        {!loading && !error && dataLoaded && analyses.length > 0 && (
          <>
            {/* Formulaire d'ajout */}
            <div className="add-analysis-section">
              <h2>Ajouter une Nouvelle Analyse</h2>
              <div className="add-form">
                <input
                  type="text"
                  placeholder="Nom de l'analyse"
                  value={newAnalysis.nom}
                  onChange={(e) => handleNewAnalysisChange('nom', e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Prix"
                  value={newAnalysis.prix}
                  onChange={(e) => handleNewAnalysisChange('prix', e.target.value)}
                />
                <select
                  value={newAnalysis.type}
                  onChange={(e) => handleNewAnalysisChange('type', e.target.value)}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
                <button className="add-btn" onClick={handleAddAnalysis}>
                  ➕ Ajouter
                </button>
              </div>
            </div>

            {/* Gestion des analyses avec défilement */}
            <div className="analyses-management-container">
              <div className="analyses-management">
                <h2>Analyses par Catégorie ({analyses.length} analyses)</h2>

                <div className="categories-container">
                  {categories.length === 0 ? (
                    <div className="no-categories">
                      <p>Aucune catégorie disponible</p>
                    </div>
                  ) : (
                    categories.map(category => {
                      const categoryAnalyses = getAnalysesByCategory(category);
                      return (
                        <div key={category} className="category-management">
                          <h3 className="category-title">
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                            <span className="analysis-count"> ({categoryAnalyses.length})</span>
                          </h3>
                          <div className="analyses-list">
                            {categoryAnalyses.length === 0 ? (
                              <div className="no-analyses">
                                <p>Aucune analyse dans cette catégorie</p>
                              </div>
                            ) : (
                              categoryAnalyses.map(analysis => (
                                <div key={analysis.id} className="analysis-item">
                                  {editingAnalysis?.id === analysis.id ? (
                                    <div className="edit-form">
                                      <input
                                        type="text"
                                        value={editingAnalysis.nom || ''}
                                        onChange={(e) => handleEditingAnalysisChange('nom', e.target.value)}
                                        placeholder="Nom de l'analyse"
                                      />
                                      <input
                                        type="number"
                                        value={editingAnalysis.prix || ''}
                                        onChange={(e) => handleEditingAnalysisChange('prix', e.target.value)}
                                        placeholder="Prix"
                                      />
                                      <select
                                        value={editingAnalysis.type || ''}
                                        onChange={(e) => handleEditingAnalysisChange('type', e.target.value)}
                                      >
                                        <option value="">Catégorie</option>
                                        {categories.map(cat => (
                                          <option key={cat} value={cat}>
                                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                          </option>
                                        ))}
                                      </select>
                                      <button className="save-btn-form" onClick={handleSaveEdit}>
                                        💾
                                      </button>
                                      <button className="cancel-btn" onClick={() => setEditingAnalysis(null)}>
                                        ❌
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="analysis-info">
                                        <span className="analysis-name">{analysis.nom}</span>
                                        <span className="analysis-price">{analysis.prix.toLocaleString()} FCFA</span>
                                      </div>
                                      <div className="analysis-actions">
                                        <button
                                          className="edit-btn"
                                          onClick={() => handleEdit(analysis)}
                                        >
                                          ✏️ Modifier
                                        </button>
                                        <button
                                          className="delete-btn"
                                          onClick={() => handleDelete(analysis.id)}
                                        >
                                          🗑️ Supprimer
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
